import { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"

import { StepProps } from "components/navigation/step-nav"
import { PURCHASE_ROUTES, PRIVATE_ROUTES, routeBasenames } from "routes"

import { useCartState } from "shared/cart/context"
import { getInsuranceProducts } from "shared/products/helpers"

const flowSteps = [
  {
    paths: ["build-your-plan", "choose-plan"],
    label: "Your Plan",
    labelMobile: "Plan"
  },
  {
    paths: ["your-info", "your-info-consultation", "create-account"],
    label: "Your Info",
    labelMobile: "Info"
  },
  {
    paths: ["checkout"],
    label: "Checkout"
  },
  {
    paths: ["confirm-your-consultation", "consultation-quiz", "confirm-terms"],
    label: "Consultation"
  }
]

const pharmacySteps = [
  {
    label: "Consultation Quiz",
    paths: ["headache-history", "headache-triggers", "headache-treatment", "lifestyle", "medical-history"]
  },
  { label: "Upload Media", paths: ["video-upload", "id-upload"] },
  { label: "Choose Pharmacy", paths: ["pharmacy-confirmation", "pharmacy-selection", "insurance-upload"] }
]

const quizSteps = [
  { label: "Migraines", paths: ["headache-history"] },
  { label: "Triggers", paths: ["headache-triggers"] },
  { label: "Treatment", paths: ["headache-treatment"] },
  { label: "Lifestyle", paths: ["lifestyle"] },
  { label: "History", paths: ["medical-history"] },
  { label: "Neuro-Baseline", paths: ["video-upload", "id-upload"] }
]

const QUIZ_ROUTES = [
  PRIVATE_ROUTES.consultationQuiz,
  PRIVATE_ROUTES.consultationQuizHeadacheHistory,
  PRIVATE_ROUTES.consultationQuizHeadacheTriggers,
  PRIVATE_ROUTES.consultationQuizHeadacheTreatment,
  PRIVATE_ROUTES.consultationQuizLifestyle,
  PRIVATE_ROUTES.consultationQuizMedicalHistory,
  PRIVATE_ROUTES.consultationQuizVideoUpload,
  PRIVATE_ROUTES.consultationQuizVideoUploadIntro,
  PRIVATE_ROUTES.consultationQuizMotorSkills,
  PRIVATE_ROUTES.consultationQuizCranialNerve,
  PRIVATE_ROUTES.consultationQuizIdUpload,
  PRIVATE_ROUTES.consultationQuizPharmacyConfirmation,
  PRIVATE_ROUTES.consultationQuizPharmacySelection,
  PRIVATE_ROUTES.consultationQuizInsuranceUpload
]

const FLOW_ROUTES = [
  PURCHASE_ROUTES.choosePlan,
  PURCHASE_ROUTES.buildYourPlan,
  PURCHASE_ROUTES.yourInfo,
  PURCHASE_ROUTES.yourInfoConsultation,
  PRIVATE_ROUTES.createAccount,
  PRIVATE_ROUTES.checkout,
  PRIVATE_ROUTES.confirmTerms
]

const removeBasename = (pathname: string) => {
  // Cut out our basenames to get pure pathname
  const pattern = `${routeBasenames.purchase}|${routeBasenames.getStarted}`
  const regExp = new RegExp(pattern, "gi")

  return pathname.replace(regExp, "")
}

export const useStepNavSteps = () => {
  const [steps, setSteps] = useState<StepProps[] | undefined>(undefined)
  const { pathname } = useLocation()
  const {
    state: { cartItems }
  } = useCartState()

  const hasInsuranceProduct = cartItems.find(({ drugType }) => getInsuranceProducts().includes(drugType))

  useEffect(() => {
    const cleanPathname = removeBasename(pathname)

    if (FLOW_ROUTES.includes(cleanPathname)) {
      if (steps !== flowSteps) {
        setSteps(flowSteps)
      }
    } else if (QUIZ_ROUTES.includes(cleanPathname)) {
      if (steps !== quizSteps) {
        setSteps(hasInsuranceProduct ? pharmacySteps : quizSteps)
      }
    } else {
      setSteps([])
    }
  }, [pathname, hasInsuranceProduct])

  return steps
}
